import { CORRECTABLE_TYPES, D, fiscalDate, fiscalYear, money, normalizeNuit, NUIT_RE } from '@makila/fiscal';
import { importSPKI, jwtVerify } from 'jose';
import { assertAmount, CashError, cashReport, countTotal } from './cash.js';
import { nextEvent } from './outbox.js';
import { applyGlobalDiscount, cartTotals, checkDiscountPct, hasDiscount, settle } from './sale.js';
import { issueInSeries, openSeries } from './series.js';
import { billableItems, mergeOrder } from './orders.js';
import { deliveredBy, serialProblem } from './services.js';
import { pharmacyProblem } from './pharmacy.js';
import { nightOf, ticketLines } from './door.js';
export class TerminalError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'TerminalError';
    }
}
const addDays = (isoDate, days) => {
    const d = new Date(`${isoDate}T12:00:00Z`);
    d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};
/**
 * Emite uma venda (VD/FR a pronto; FT a crédito). Calcula, reparte o
 * pagamento, numera, assina, grava e põe na fila — numa só transacção.
 *
 * FT a crédito (decisão do utilizador, 30/10/2026): só a cliente da ficha com
 * NUIT e limite aprovado; acima do limite, só com autorização de um gerente.
 * Não leva pagamento: fica em dívida até ao recibo (emitReceipt).
 */
export async function emitSale(store, who, signer, sale, newId = () => globalThis.crypto.randomUUID()) {
    if (!sale.licence.sell)
        throw new TerminalError('cannot_sell', sale.licence.reason ?? 'Licença sem venda');
    if (!sale.lines.length)
        throw new TerminalError('empty_cart', 'Sem artigos na venda');
    // A factura-recibo identifica o cliente; sem cliente é venda a dinheiro (VD).
    if (sale.docType === 'FR' && !sale.customer?.name.trim())
        throw new TerminalError('customer_required', 'A factura-recibo precisa do nome do cliente');
    const customer = sale.customer ? checkCustomer(sale.customer) : null;
    const now = sale.now ?? new Date();
    // Cálculo e pagamento antes da transacção: um erro aqui não toca em nada.
    for (const l of sale.lines)
        checkDiscountPct(l.discountPct ?? 0);
    const lines = applyGlobalDiscount(sale.lines, sale.globalDiscountPct ?? 0);
    if (hasDiscount(lines) && !sale.discountApprovedBy)
        throw new TerminalError('approval_required', 'Os descontos precisam da autorização de um gerente');
    // Pontos: só com cliente da ficha; o desconto das linhas tem de ser o dos pontos (applyLoyalty).
    if (lines.some((l) => l.loyalty)) {
        if (!customer?.id || !sale.loyaltyPoints)
            throw new TerminalError('loyalty', 'Os pontos só se usam com um cliente identificado');
        if (hasDiscount(lines) || sale.globalDiscountPct && sale.globalDiscountPct !== '0')
            throw new TerminalError('loyalty', 'Os pontos não se juntam a outros descontos');
    }
    const rxProblem = pharmacyProblem(lines, sale.prescription, fiscalDate(now));
    if (rxProblem)
        throw new TerminalError('pharmacy', rxProblem);
    const snProblem = serialProblem(lines);
    if (snProblem)
        throw new TerminalError('serial', snProblem);
    const totals = cartTotals(lines);
    const credit = sale.docType === 'FT';
    if (credit) {
        if (!customer?.id || !customer.nuit)
            throw new TerminalError('nuit_required', 'A venda a crédito precisa de um cliente da ficha com NUIT');
        if (!sale.credit || sale.credit.limit === null)
            throw new TerminalError('credit_none', `${customer.name} não tem crédito aprovado: o limite define-se na gestão`);
        if (sale.tenders.length)
            throw new TerminalError('credit_exceeds', 'A venda a crédito não leva pagamento: o cliente paga depois, com recibo');
        const after = new D(sale.credit.balance).plus(totals.grossTotal);
        if (after.greaterThan(sale.credit.limit) && !sale.creditApprovedBy) {
            throw new TerminalError('credit_limit', `Passa o limite de ${money(sale.credit.limit)} MT (em dívida ${money(sale.credit.balance)} MT): precisa de um gerente`);
        }
    }
    const pay = credit ? { payments: [], tendered: '0.00', change: '0.00' } : settle(totals.grossTotal, sale.tenders);
    let tickets = [];
    try {
        const seen = new Set();
        tickets = ticketLines(lines).flatMap((t) => Array.from({ length: t.count }, () => {
            let code = newVoucherCode();
            while (seen.has(code))
                code = newVoucherCode();
            seen.add(code);
            return { code, line: t.index, admits: t.admits, eventDate: t.eventDate };
        }));
    }
    catch (e) {
        throw new TerminalError('ticket', e.message);
    }
    return store.transaction(async (tx) => {
        const session = await tx.openSession();
        if (!session || session.id !== sale.cashSessionId)
            throw new CashError('no_session', 'Abra a caixa antes de vender');
        const year = fiscalYear(now);
        const series = (await tx.getSeries(sale.docType, year, who.training))
            ?? openSeries({ seriesId: newId(), docType: sale.docType, year, storeCode: who.storeCode, terminalCode: who.terminalCode, training: who.training, keyId: who.keyId });
        const r = await issueInSeries(series, { grossTotal: totals.grossTotal, now }, signer);
        const doc = {
            ...r.issued,
            docType: sale.docType,
            prefix: r.series.prefix,
            training: r.series.training,
            lines: lines.map((l, i) => ({ ...l, netAmount: totals.lines[i].netAmount, taxAmount: totals.lines[i].taxAmount, totalAmount: totals.lines[i].totalAmount })),
            netTotal: totals.netTotal,
            taxTotal: totals.taxTotal,
            taxSummary: totals.taxSummary,
            payments: pay.payments,
            tendered: pay.tendered,
            change: pay.change,
            customer,
            operatorId: sale.operatorId,
            cashSessionId: session.id,
            ...(hasDiscount(lines) ? { discountApprovedBy: sale.discountApprovedBy } : {}),
            ...(sale.prescription && lines.some((l) => l.requiresPrescription || l.controlled) ? { prescription: sale.prescription } : {}),
            ...(lines.some((l) => l.loyalty) && customer?.id ? { loyalty: { customerId: customer.id, pointsUsed: sale.loyaltyPoints } } : {}),
            ...(credit ? { dueDate: addDays(r.issued.issueDate, sale.credit.termsDays), ...(sale.creditApprovedBy ? { approvedBy: sale.creditApprovedBy } : {}) } : {}),
            ...(tickets.length ? { tickets } : {}),
        };
        await tx.putSeries(r.series);
        await tx.saveDocument(doc);
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'document.issued', payload: doc, now }));
        if (sale.order) {
            // A conta e a comanda juntas: nunca um documento emitido com a comanda ainda por pagar (nem o contrário).
            const o = await tx.getOrder(sale.order.id);
            if (!o || o.status !== 'open')
                throw new TerminalError('order_closed', 'Esta comanda já não está aberta');
            const at = now.toISOString();
            const pick = new Set(billableItems(o).filter((i) => !sale.order.itemIds || sale.order.itemIds.includes(i.id)).map((i) => i.id));
            if (!pick.size)
                throw new TerminalError('empty_cart', 'Nada por pagar nesta comanda');
            const items = o.items.map((i) => (pick.has(i.id) ? { ...i, paidIn: doc.fullNumber, updatedAt: at } : i));
            const paid = { ...o, items, documents: [...o.documents, doc.fullNumber], updatedAt: at };
            // Ordem de serviço cobrada por inteiro: fica entregue.
            const closed = billableItems(paid).length === 0 ? deliveredBy({ ...paid, status: 'closed', closedAt: at }, sale.operatorId, at) : paid;
            await tx.putOrder(closed);
            await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'order.snapshot', payload: closed, now }));
        }
        return doc;
    });
}
/**
 * Grava uma alteração à comanda (abrir, juntar itens, enviar para a cozinha,
 * cancelar, mudar de mesa…): junta-a ao que o terminal já tinha e põe o
 * retrato na fila para a nuvem e para as outras caixas.
 */
export async function saveOrder(store, who, order, now = new Date()) {
    return store.transaction(async (tx) => {
        const cur = await tx.getOrder(order.id);
        // Fechada (conta emitida, ou juntada a outra mesa) não leva mais alterações.
        if (cur && cur.status !== 'open')
            throw new TerminalError('order_closed', 'Esta comanda já foi fechada');
        const merged = cur ? mergeOrder(cur, order) : order;
        await tx.putOrder(merged);
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'order.snapshot', payload: merged, now }));
        return merged;
    });
}
/** Nome limpo e NUIT de 9 algarismos (ou nenhum). */
export function checkCustomer(c) {
    const name = c.name.trim();
    if (!name)
        throw new TerminalError('customer_required', 'Indique o nome do cliente');
    const nuit = normalizeNuit(c.nuit);
    if (nuit && !NUIT_RE.test(nuit))
        throw new TerminalError('invalid_nuit', `NUIT inválido: ${c.nuit} (9 algarismos)`);
    return { ...(c.id ? { id: c.id } : {}), name, nuit };
}
/** Numera, assina, grava e põe na fila um documento já calculado (comum a PF, NC, RE e às reservas). */
export async function issueDocument(tx, who, signer, docType, now, newId, build, grossTotal) {
    const year = fiscalYear(now);
    const series = (await tx.getSeries(docType, year, who.training))
        ?? openSeries({ seriesId: newId(), docType, year, storeCode: who.storeCode, terminalCode: who.terminalCode, training: who.training, keyId: who.keyId });
    const r = await issueInSeries(series, { grossTotal, now }, signer);
    const doc = { ...r.issued, docType, prefix: r.series.prefix, training: r.series.training, ...build(r.issued) };
    await tx.putSeries(r.series);
    await tx.saveDocument(doc);
    await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'document.issued', payload: doc, now }));
    return doc;
}
/**
 * Pró-forma (orçamento): sem valor fiscal, sem pagamento, fora da caixa e das
 * vendas. Numerada na sua série, como no Makila.
 */
export async function emitProforma(store, who, signer, pf, newId = () => globalThis.crypto.randomUUID()) {
    if (!pf.lines.length)
        throw new TerminalError('empty_cart', 'Sem artigos na pró-forma');
    const customer = pf.customer ? checkCustomer(pf.customer) : null;
    const totals = cartTotals(pf.lines);
    const now = pf.now ?? new Date();
    return store.transaction((tx) => issueDocument(tx, who, signer, 'PF', now, newId, () => ({
        lines: pf.lines.map((l, i) => ({ ...l, netAmount: totals.lines[i].netAmount, taxAmount: totals.lines[i].taxAmount, totalAmount: totals.lines[i].totalAmount })),
        netTotal: totals.netTotal, taxTotal: totals.taxTotal, taxSummary: totals.taxSummary,
        payments: [], tendered: '0.00', change: '0.00', customer, operatorId: pf.operatorId, cashSessionId: null,
    }), totals.grossTotal));
}
/**
 * Nota de crédito (devolução) — a única forma de corrigir um documento emitido
 * (CIVA art. 27; regra do Makila). Referencia a origem, tem motivo e é
 * autorizada por um gerente. Linha a linha, nunca mais do que ainda falta
 * creditar. O reembolso sai pelos meios indicados (o numerário sai da gaveta e
 * o Z desconta-o).
 *
 * `alreadyCredited`: o que a nuvem diz já ter sido creditado (documentos de
 * outro terminal); junta-se ao que este terminal sabe — vale o maior.
 */
export async function emitCreditNote(store, who, signer, nc, newId = () => globalThis.crypto.randomUUID()) {
    if (!nc.licence.sell)
        throw new TerminalError('cannot_sell', nc.licence.reason ?? 'Licença sem venda');
    const o = nc.origin;
    if (!CORRECTABLE_TYPES.includes(o.docType))
        throw new TerminalError('not_correctable', `${o.fullNumber} não se corrige com nota de crédito`);
    if (o.training !== who.training)
        throw new TerminalError('training_mismatch', 'Documento de formação e real não se misturam');
    if (!nc.reason.trim())
        throw new TerminalError('reason_required', 'Indique o motivo da devolução');
    if (!nc.approvedBy)
        throw new TerminalError('approval_required', 'A devolução precisa da autorização de um gerente');
    const wanted = nc.lines.filter((l) => new D(l.quantity).greaterThan(0));
    if (!wanted.length)
        throw new TerminalError('empty_cart', 'Escolha o que é devolvido');
    // NC a cliente identificado leva o NUIT dele (o Consumidor Final não): pedido no caixa se a ficha não o tinha.
    let customer = o.customer;
    if (customer && !customer.nuit) {
        const nuit = normalizeNuit(nc.customerNuit ?? null);
        if (!nuit || !NUIT_RE.test(nuit))
            throw new TerminalError('nuit_required', `A nota de crédito a ${customer.name} precisa do NUIT do cliente (CIVA art. 27 n.º 5 a)`);
        customer = { ...customer, nuit };
    }
    const now = nc.now ?? new Date();
    return store.transaction(async (tx) => {
        const session = await tx.openSession();
        if (!session || session.id !== nc.cashSessionId)
            throw new CashError('no_session', 'Abra a caixa antes de devolver');
        const local = await tx.creditedQuantities(o.seriesId, o.number);
        const lines = wanted.map((w) => {
            const src = o.lines[w.originLine];
            if (!src)
                throw new TerminalError('credit_exceeds', `Linha ${w.originLine + 1} não existe em ${o.fullNumber}`);
            const done = D.max(new D(local[w.originLine] ?? 0), new D(nc.alreadyCredited?.[w.originLine] ?? 0));
            const left = new D(src.quantity).minus(done);
            if (new D(w.quantity).greaterThan(left))
                throw new TerminalError('credit_exceeds', `${src.description}: só falta devolver ${left.toString().replace('.', ',')}`);
            const { netAmount: _n, taxAmount: _t, totalAmount: _tt, ...base } = src;
            return { ...base, quantity: w.quantity, originLine: w.originLine };
        });
        const totals = cartTotals(lines);
        // NC de uma FT a crédito: abate a dívida da conta corrente; não sai dinheiro.
        if (o.docType === 'FT' && nc.refunds.length)
            throw new TerminalError('refund_mismatch', 'A devolução de uma factura a crédito abate a conta corrente: não há reembolso em dinheiro');
        const pay = o.docType === 'FT' ? { payments: [], tendered: '0.00', change: '0.00' } : settle(totals.grossTotal, nc.refunds);
        if (new D(pay.change).greaterThan(0))
            throw new TerminalError('refund_mismatch', `O reembolso tem de ser exactamente ${money(totals.grossTotal)} MT`);
        return issueDocument(tx, who, signer, 'NC', now, newId, () => ({
            lines: lines.map((l, i) => ({ ...l, netAmount: totals.lines[i].netAmount, taxAmount: totals.lines[i].taxAmount, totalAmount: totals.lines[i].totalAmount })),
            netTotal: totals.netTotal, taxTotal: totals.taxTotal, taxSummary: totals.taxSummary,
            payments: pay.payments, tendered: pay.tendered, change: '0.00',
            customer, operatorId: nc.operatorId, cashSessionId: session.id,
            origin: { seriesId: o.seriesId, number: o.number, fullNumber: o.fullNumber, issueDate: o.issueDate, docType: o.docType },
            reason: nc.reason.trim(), approvedBy: nc.approvedBy,
        }), totals.grossTotal);
    });
}
/**
 * Cliente criado no caixa (mesmo sem rede): fica logo disponível neste
 * terminal e segue para a nuvem como evento; os outros recebem-no pelo catálogo.
 */
export async function createCustomer(store, who, c, newId = () => globalThis.crypto.randomUUID()) {
    const clean = checkCustomer({ name: c.name, nuit: c.nuit ?? null });
    const customer = { id: newId(), name: clean.name, nuit: clean.nuit, phone: c.phone?.trim() || null };
    const now = c.now ?? new Date();
    await store.transaction(async (tx) => {
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'customer.created', payload: customer, now }));
    });
    // Disponível já neste terminal (sem mexer no cursor do catálogo).
    await store.applyCatalog([{ type: 'customer', id: customer.id, version: 0, data: { ...customer, active: true } }], await store.catalogCursor());
    return customer;
}
// ── Caixa ───────────────────────────────────────────────────────────────────
/** Abre a caixa com o fundo (numerário que fica na gaveta para trocos). */
export async function openCashSession(store, who, p, newId = () => globalThis.crypto.randomUUID()) {
    const float = assertAmount(p.openingFloat, true);
    const now = p.now ?? new Date();
    return store.transaction(async (tx) => {
        const open = await tx.openSession();
        if (open)
            throw new CashError('session_open', 'A caixa já está aberta');
        const s = {
            id: newId(), terminalId: who.terminalId, operatorId: p.operatorId, openedAt: now.toISOString(), openingFloat: float, status: 'open',
            closedAt: null, closedBy: null, zNumber: null, counted: null, countedCash: null, expectedCash: null, difference: null,
        };
        await tx.putSession(s);
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'cash.session_opened', payload: s, now }));
        return s;
    });
}
/** Sangria, reforço ou abertura de gaveta sem venda — sempre com motivo e operador. */
export async function recordCashMovement(store, who, p, newId = () => globalThis.crypto.randomUUID()) {
    const amount = assertAmount(p.amount, p.kind === 'drawer_open');
    if (p.kind === 'tip' && !p.beneficiaryId)
        throw new CashError('reason_required', 'Indique o empregado a quem é a gorjeta');
    const reason = p.kind === 'tip' && !p.reason.trim() ? 'Gorjeta' : p.reason;
    if (!reason.trim())
        throw new CashError('reason_required', 'Indique o motivo');
    const now = p.now ?? new Date();
    return store.transaction(async (tx) => {
        const s = await tx.openSession();
        if (!s)
            throw new CashError('no_session', 'A caixa não está aberta');
        const m = {
            id: newId(), sessionId: s.id, kind: p.kind, amount: p.kind === 'drawer_open' ? '0.00' : amount, reason: reason.trim(), operatorId: p.operatorId, at: now.toISOString(),
            ...(p.kind === 'tip' ? { beneficiaryId: p.beneficiaryId, method: p.method ?? 'NU' } : {}),
        };
        await tx.addMovement(m);
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'cash.movement', payload: m, now }));
        return m;
    });
}
/** Relatório X da sessão aberta (sem fechar). */
export async function cashReportX(store) {
    return store.transaction(async (tx) => {
        const s = await tx.openSession();
        return s ? cashReport(s, await tx.sessionDocuments(s.id), await tx.sessionMovements(s.id)) : null;
    });
}
/**
 * Fecho (relatório Z): conta o numerário por denominação, calcula esperado e
 * diferença, numera o Z e fecha a sessão — tudo numa transacção.
 */
export async function closeCashSession(store, who, p) {
    const countedCash = countTotal(p.counted);
    const now = p.now ?? new Date();
    return store.transaction(async (tx) => {
        const s = await tx.openSession();
        if (!s)
            throw new CashError('no_session', 'A caixa não está aberta');
        const docs = await tx.sessionDocuments(s.id);
        const moves = await tx.sessionMovements(s.id);
        const x = cashReport(s, docs, moves);
        const closed = {
            ...s, status: 'closed', closedAt: now.toISOString(), closedBy: p.operatorId, zNumber: (await tx.lastZ()) + 1,
            counted: p.counted, countedCash, expectedCash: x.expectedCash, difference: money(new D(countedCash).minus(x.expectedCash)),
        };
        await tx.putSession(closed);
        const z = cashReport(closed, docs, moves);
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'cash.session_closed', payload: { session: closed, report: z }, now }));
        return z;
    });
}
/**
 * Verifica a licença sem rede, com a chave pública recebida na activação.
 * Expirada ou adulterada → não vende (mas fecha o caixa e sincroniza).
 */
export async function licenceStatus(token, publicKeyPem, terminalId, now = new Date()) {
    if (!token)
        return { sell: false, reason: 'Terminal sem licença. Ligue-o à internet.', expiresAt: null };
    try {
        const { payload } = await jwtVerify(token, await importSPKI(publicKeyPem, 'EdDSA'), {
            issuer: 'makila-pos', audience: 'makila-pos-terminal', subject: terminalId, currentDate: now,
        });
        const expiresAt = new Date((payload.exp ?? 0) * 1000).toISOString();
        if (payload.sell !== true)
            return { sell: false, reason: 'A subscrição não permite vender. Regularize-a no backoffice.', expiresAt };
        return { sell: true, reason: null, expiresAt };
    }
    catch (e) {
        const expired = e.code === 'ERR_JWT_EXPIRED';
        return { sell: false, reason: expired ? 'O terminal está há demasiado tempo sem internet. Ligue-o para renovar a licença.' : 'Licença inválida. Active o terminal de novo.', expiresAt: null };
    }
}
/**
 * Uma volta de sincronização: envia a fila em lotes e depois puxa o catálogo.
 * Pode correr a qualquer momento e quantas vezes for preciso — a nuvem é
 * idempotente por seq, por isso uma resposta perdida só causa um reenvio.
 */
export async function syncOnce(store, transport, batch = 200) {
    const report = { sent: 0, acked: 0, review: 0, catalogItems: 0, lastSeq: 0 };
    for (let guard = 0; guard < 1000; guard++) {
        const pending = await store.pendingOutbox(batch);
        if (!pending.length)
            break;
        const r = await transport.push(pending);
        report.sent += pending.length;
        await store.saveLicence(r.licence);
        const before = pending[0].seq - 1;
        await store.setAcked(r.lastSeq);
        report.lastSeq = r.lastSeq;
        report.acked += r.results.filter((x) => x.status !== 'gap').length;
        report.review += r.results.filter((x) => x.status === 'accepted' && x.integrity === 'review').length;
        // Nada mudou do lado de lá: pára (não fica em ciclo); a próxima volta tenta de novo.
        if (r.lastSeq === before)
            break;
    }
    for (let guard = 0; guard < 1000; guard++) {
        const since = await store.catalogCursor();
        const c = await transport.pullCatalog(since);
        if (c.items.length)
            await store.applyCatalog(c.items, c.nextCursor);
        report.catalogItems += c.items.length;
        if (!c.more)
            break;
    }
    for (let guard = 0; transport.pullOrders && guard < 1000; guard++) {
        const o = await transport.pullOrders(await store.ordersCursor());
        if (o.items.length || o.nextCursor > await store.ordersCursor())
            await store.applyOrders(o.items, o.nextCursor);
        if (!o.more)
            break;
    }
    return report;
}
/** Transporte HTTP para a API do POS (fetch existe no WebView, no Tauri e no Node). */
export function httpTransport(baseUrl, deviceSecret, f = fetch) {
    const req = async (path, init) => {
        const res = await f(`${baseUrl}${path}`, { ...init, headers: { ...(init?.body !== undefined ? { 'content-type': 'application/json' } : {}), authorization: `Terminal ${deviceSecret}`, ...init?.headers } });
        const body = await res.json().catch(() => null);
        if (!res.ok || !body)
            throw new SyncError(body?.code ?? `http_${res.status}`, body?.message ?? `Falha na sincronização (${res.status})`);
        return body;
    };
    return {
        push: (events) => req('/sync/events', { method: 'POST', body: JSON.stringify({ events }) }),
        pullCatalog: (since) => req(`/sync/catalog?since=${since}`),
        pullOrders: (since) => req(`/sync/orders?since=${since}`),
    };
}
export class SyncError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'SyncError';
    }
}
/**
 * Recibo (RE): o cliente paga (parte de) a conta corrente. Sem linhas nem IVA
 * (o IVA foi da factura); o dinheiro entra na caixa como numa venda. A nuvem
 * liquida as facturas mais antigas primeiro. O valor não passa do saldo que a
 * caixa conhece (o pagamento adiantado faz-se com outra venda).
 */
export async function emitReceipt(store, who, signer, re, newId = () => globalThis.crypto.randomUUID()) {
    if (!re.licence.sell)
        throw new TerminalError('cannot_sell', re.licence.reason ?? 'Licença sem venda');
    const customer = checkCustomer(re.customer);
    if (!customer.id)
        throw new TerminalError('customer_required', 'O recibo é de um cliente da ficha');
    const amount = new D(re.amount);
    if (!amount.greaterThan(0) || amount.decimalPlaces() > 2)
        throw new TerminalError('receipt_amount', 'Valor do recibo inválido');
    if (amount.greaterThan(re.balance))
        throw new TerminalError('receipt_amount', `O valor passa o que o cliente deve (${money(re.balance)} MT)`);
    const total = money(amount);
    const pay = settle(total, re.tenders);
    const now = re.now ?? new Date();
    return store.transaction(async (tx) => {
        const session = await tx.openSession();
        if (!session || session.id !== re.cashSessionId)
            throw new CashError('no_session', 'Abra a caixa antes de receber');
        return issueDocument(tx, who, signer, 'RE', now, newId, () => ({
            lines: [], netTotal: total, taxTotal: '0.00', taxSummary: [],
            payments: pay.payments, tendered: pay.tendered, change: pay.change, customer, operatorId: re.operatorId, cashSessionId: session.id,
            receipt: { balanceBefore: money(re.balance) },
        }), total);
    });
}
export class PrepaidError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.code = code;
        this.name = 'PrepaidError';
    }
}
/**
 * Sessão de um pacote (10 massagens…): fica registada e a nuvem abate-a.
 * Recusa um pacote vazio ou fora de validade (pelo que a caixa sabe).
 */
export async function usePackageSession(store, who, u) {
    const now = u.now ?? new Date();
    if (u.pkg.remaining <= 0)
        throw new PrepaidError('package_empty', `${u.pkg.name}: já não há sessões`);
    if (u.pkg.expiresAt && u.pkg.expiresAt < fiscalDate(now))
        throw new PrepaidError('package_expired', `${u.pkg.name}: o pacote expirou a ${u.pkg.expiresAt.split('-').reverse().join('/')}`);
    return store.transaction(async (tx) => {
        const ev = nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'package.used', now,
            payload: { packageId: u.pkg.id, customerId: u.customerId, serviceId: u.pkg.serviceId, staffId: u.staffId ?? null, appointmentId: u.appointmentId ?? null, operatorId: u.operatorId, at: now.toISOString() } });
        await tx.appendOutbox(ev);
        return { id: ev.id };
    });
}
/** Mensalidade em dia? (a data é a da última sincronização do cliente). */
export function membershipState(memberUntil, now = new Date()) {
    if (!memberUntil)
        return { ok: false, until: null, daysLeft: null };
    const today = fiscalDate(now);
    const days = Math.round((Date.parse(`${memberUntil}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 86_400_000);
    return { ok: days >= 0, until: memberUntil, daysLeft: days };
}
/** Entrada no ginásio: fica registada, em dia ou não (quem deixa entrar em atraso fica identificado). */
export async function recordEntry(store, who, e) {
    const now = e.now ?? new Date();
    await store.transaction(async (tx) => {
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'access.entry', now, payload: { customerId: e.customerId, ok: e.ok, operatorId: e.operatorId, at: now.toISOString() } }));
    });
}
/** Bar: "Garrafa vazia" — fecha a garrafa aberta deste artigo na loja; o que faltava vender é perda. */
export async function bottleEmpty(store, who, e) {
    const now = e.now ?? new Date();
    await store.transaction(async (tx) => {
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'bottle.empty', now, payload: { productId: e.productId, operatorId: e.operatorId, at: now.toISOString() } }));
    });
}
/** Porta: bilhete lido e aceite (ticketCheck); a nuvem marca-o usado e conta as pessoas. */
export async function admitTicket(store, who, e) {
    const now = e.now ?? new Date();
    await store.transaction(async (tx) => {
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'ticket.used', now, payload: { code: e.code, admits: e.admits, operatorId: e.operatorId, at: now.toISOString(), night: nightOf(now) } }));
    });
}
/** Porta: convidados (entram sem pagar); contam para a lotação da noite. */
export async function doorGuest(store, who, e) {
    if (!Number.isInteger(e.people) || e.people < 1 || e.people > 50)
        throw new TerminalError('ticket', 'Número de convidados inválido (1 a 50)');
    if (!e.note.trim())
        throw new TerminalError('reason_required', 'Diga de quem são os convidados');
    const now = e.now ?? new Date();
    await store.transaction(async (tx) => {
        await tx.appendOutbox(nextEvent({ terminalId: who.terminalId, lastSeq: await tx.lastSeq(), type: 'door.guest', now, payload: { people: e.people, note: e.note.trim().slice(0, 200), operatorId: e.operatorId, at: now.toISOString(), night: nightOf(now) } }));
    });
}
// ── Vales-oferta (decisão F2): adiantamento sem IVA; o IVA sai quando se usa ──
const VOUCHER_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // sem 0/O, 1/I: lê-se bem em voz alta e no papel
/** Código do vale: XXXX-XXXX, aleatório. */
export function newVoucherCode(rand = (n) => globalThis.crypto.getRandomValues(new Uint8Array(n))) {
    const b = rand(8);
    const c = [...b].map((x) => VOUCHER_ALPHABET[x % VOUCHER_ALPHABET.length]).join('');
    return `${c.slice(0, 4)}-${c.slice(4)}`;
}
export const VOUCHER_RE = /^Vale ([A-Z2-9]{4}-[A-Z2-9]{4})$/;
/** Referência do pagamento com vale (meio "Outro"): a nuvem abate-o pelo código. */
export const voucherReference = (code) => `Vale ${code}`;
/** Quanto do vale se pode usar nesta compra (0 se expirado, sem saldo ou desactivado). */
export function voucherUsable(g, due, now = new Date()) {
    if (!g || !g.active)
        return { amount: '0.00', reason: 'Vale desconhecido ou anulado' };
    if (g.expiresAt && g.expiresAt < fiscalDate(now))
        return { amount: '0.00', reason: `Vale expirado a ${g.expiresAt.split('-').reverse().join('/')}` };
    if (!new D(g.balance).greaterThan(0))
        return { amount: '0.00', reason: 'O vale já não tem saldo' };
    return { amount: money(D.min(new D(g.balance), new D(due))), reason: null };
}
/**
 * Venda de um vale-oferta: recibo de adiantamento (RE) sem linhas nem IVA,
 * com o código do vale. Entra dinheiro na caixa; não é venda.
 */
export async function emitVoucher(store, who, signer, v, newId = () => globalThis.crypto.randomUUID()) {
    if (!v.licence.sell)
        throw new TerminalError('cannot_sell', v.licence.reason ?? 'Licença sem venda');
    const amount = new D(v.amount);
    if (!amount.greaterThan(0) || amount.decimalPlaces() > 2)
        throw new TerminalError('receipt_amount', 'Valor do vale inválido');
    if (!/^[A-Z2-9]{4}-[A-Z2-9]{4}$/.test(v.code))
        throw new TerminalError('receipt_amount', 'Código do vale inválido');
    const total = money(amount);
    const pay = settle(total, v.tenders);
    const now = v.now ?? new Date();
    const expiresAt = v.validityDays ? new Date(Date.parse(`${fiscalDate(now)}T12:00:00Z`) + v.validityDays * 86_400_000).toISOString().slice(0, 10) : null;
    const buyer = v.buyer?.name.trim() ? checkCustomer(v.buyer) : null;
    return store.transaction(async (tx) => {
        const session = await tx.openSession();
        if (!session || session.id !== v.cashSessionId)
            throw new CashError('no_session', 'Abra a caixa antes de vender');
        return issueDocument(tx, who, signer, 'RE', now, newId, () => ({
            lines: [], netTotal: total, taxTotal: '0.00', taxSummary: [],
            payments: pay.payments, tendered: pay.tendered, change: pay.change, customer: buyer, operatorId: v.operatorId, cashSessionId: session.id,
            voucher: { code: v.code, expiresAt },
        }), total);
    });
}
//# sourceMappingURL=terminal.js.map